import React from "react";
import { IonCard, IonCol, IonGrid, IonRow, IonText } from "@ionic/react";
import {
  NumberDecimal,
  NumberMoney,
} from "../../components/NumberMoney/NumberMoney";
import styled from "styled-components";
import { useCart } from "./useCart";

interface IOrderPrice {
  deliveryInfo?: boolean;
}

interface IRow {
  show: boolean;
  label: React.ReactNode;
  unit: React.ReactNode;
  qty: number;
  total: number;
}

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th,
  td {
    padding: 5px;
    vertical-align: top;
    text-align: left;
    font-weight: normal;
  }
  thead th {
    background-color: var(--ion-color-light-tint);
  }
  thead th:first-child {
    border-radius: 5px 0 0 5px;
  }
  thead th:last-child {
    border-radius: 0 5px 5px 0;
  }
  .num {
    text-align: right;
  }
  .qty {
    text-align: center;
    width: 1%;
  }

  /* Small phones: four columns with two RSD amounts need every pixel. */
  @media (max-width: 400px) {
    font-size: 13px;
    th,
    td {
      padding: 4px 3px;
    }
  }
`;
export const OrderPriceDetails = (props: IOrderPrice) => {
  const {
    cartWeight,
    easelWood,
    easelAlum,
    framesTotal,
    framesAssembled,
    colorPack,
    brandedBag,
    reserveCount,
    pricePictures,
    minMaxPicturePrice,
    pricePicturesKids,
    minMaxPicturePriceKids,
    priceEaselsWood,
    priceEaselsAlum,
    priceFrames,
    priceFramesAssembled,
    priceColorPack,
    priceBrandedBag,
    priceTotal,
    countPrice,
    countPriceKids,
    equipmentPrice,
  } = useCart();

  // One row per ordered product type; shipping is always last.
  const rows: IRow[] = [
    {
      show: Boolean(countPrice),
      label: "Set za bojenje 40x50cm",
      unit: (
        <NumberMoney
          minValue={minMaxPicturePrice().min}
          value={minMaxPicturePrice().max}
        />
      ),
      qty: countPrice,
      total: pricePictures(),
    },
    {
      show: Boolean(countPriceKids),
      label: "Set za bojenje 20x20cm sa blind ramom",
      unit: (
        <NumberMoney
          minValue={minMaxPicturePriceKids().min}
          value={minMaxPicturePriceKids().max}
        />
      ),
      qty: countPriceKids,
      total: pricePicturesKids(),
    },
    {
      show: Boolean(framesTotal),
      label: "Drveni (blind) ram 40x50cm",
      unit: <NumberMoney value={equipmentPrice.frame} />,
      qty: framesTotal,
      total: priceFrames(),
    },
    {
      show: Boolean(framesAssembled),
      label: "Drveni (blind) ram 40x50cm - sklopljen",
      unit: <NumberMoney value={equipmentPrice.frameAssembled} />,
      qty: framesAssembled,
      total: priceFramesAssembled(),
    },
    // TO DO: "Usluga montiranja (blind) rama" (PRICE_FRAME_PUT_TOGETHER, priceFramesPutTogether())
    {
      show: Boolean(easelWood),
      label: "Drveni štafelaj",
      unit: <NumberMoney value={equipmentPrice.easelW} />,
      qty: easelWood,
      total: priceEaselsWood(),
    },
    {
      show: Boolean(easelAlum),
      label: "Aluminijumski štafelaj",
      unit: <NumberMoney value={equipmentPrice.easelA} />,
      qty: easelAlum,
      total: priceEaselsAlum(),
    },
    {
      show: Boolean(colorPack),
      label: "Set boja (dodatni)",
      unit: <NumberMoney value={equipmentPrice.colorPack} />,
      qty: colorPack,
      total: priceColorPack(),
    },
    {
      show: Boolean(brandedBag),
      label: "Brendirana kesa",
      unit: <NumberMoney value={equipmentPrice.brandedBag} />,
      qty: brandedBag,
      total: priceBrandedBag(),
    },
    {
      show: true,
      label: <strong>Poštarina</strong>,
      unit: <NumberMoney value={equipmentPrice.shipping} />,
      qty: 1,
      total: equipmentPrice.shipping,
    },
  ];

  return (
    <IonCard>
      {/* A table, not fixed grid columns: the price columns take the width their amounts need
          and the description wraps, so "1.900,00 RSD" never runs into the next column. */}
      <div style={{ padding: "10px 10px 0 10px" }}>
        <Table>
          <thead>
            <tr>
              <th>OPIS</th>
              <th className="num">CENA</th>
              <th className="qty">KOL.</th>
              <th className="num">UKUPNO</th>
            </tr>
          </thead>
          <tbody>
            {rows
              .filter((r) => r.show)
              .map((r, i) => (
                <tr key={i}>
                  <td>{r.label}</td>
                  <td className="num">{r.unit}</td>
                  <td className="qty">{r.qty}</td>
                  <td className="num">
                    <NumberMoney value={r.total} />
                  </td>
                </tr>
              ))}
          </tbody>
        </Table>
      </div>
      <IonGrid style={{ padding: "0 10px 10px 10px" }}>
        <IonRow
          style={{
            backgroundColor: "var(--ion-color-light-tint)",
            borderRadius: "5px",
            flexWrap: "nowrap",
          }}
        >
          <IonCol>
            <span className="b">SVE UKUPNO (sa poštarinom)</span>
          </IonCol>
          <IonCol size="auto" className="ion-text-right">
            <span className="b">
              <NumberMoney value={priceTotal()} />
            </span>
          </IonCol>
        </IonRow>
        {props.deliveryInfo && (
          <IonRow>
            <IonCol style={{ paddingTop: 15 }}>
              <IonText>
                Težina vaše porudžbine je oko:{" "}
                <span className="b">
                  <NumberDecimal value={cartWeight} /> kg
                </span>
              </IonText>
            </IonCol>
          </IonRow>
        )}
        <IonRow>
          {reserveCount > 0 && (
            <IonCol size="12">Broj rezervacija: {reserveCount}</IonCol>
          )}
        </IonRow>
      </IonGrid>
    </IonCard>
  );
};
